import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { gqlClient } from '@/api/graphql-client';
import dashboardKeys from '@/api/queries/dashboard/keyFactory';
import { graphql } from '@/gql';
import { formatMonthLabel } from '@/utils/dashboard';

const GET_DASHBOARD_PROPOSAL_STATS = graphql(`
  query GetDashboardProposalStats($input: DashboardProposalStatsInput) {
    dashboardProposalStats(input: $input) {
      sent {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      accepted {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      declined {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      timeSeries {
        month
        sentCount
        sentValue
        acceptedCount
        acceptedValue
        declinedCount
        declinedValue
      }
    }
  }
`);

export interface GetDashboardProposalStatsOptions {
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

const useGetDashboardProposalStats = (
  options?: GetDashboardProposalStatsOptions
) => {
  const { dateRange } = options ?? {};

  const { data, ...rest } = useQuery({
    queryKey: dashboardKeys.proposalStats(
      dateRange
        ? { sentAtFrom: dateRange.from, sentAtTo: dateRange.to }
        : undefined
    ),
    queryFn: () =>
      gqlClient.request(GET_DASHBOARD_PROPOSAL_STATS, {
        input: {
          sentAtFrom: dateRange?.from?.toISOString(),
          sentAtTo: dateRange?.to?.toISOString(),
        },
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Transform the data to match component expectations
  const stats = useMemo(() => {
    if (!data?.dashboardProposalStats) return null;

    const { sent, accepted, declined, timeSeries } =
      data.dashboardProposalStats;

    const transformCountAndValue = (
      item: typeof sent | typeof accepted | typeof declined
    ) => ({
      count: item.count,
      totalValue: { value: item.totalValue },
      sparkline: item.byDate.map((d) => d.value),
      sparklineValue: item.byDate.map((d) => d.value),
    });

    return {
      sent: transformCountAndValue(sent),
      accepted: transformCountAndValue(accepted),
      declined: transformCountAndValue(declined),
      timeSeries: timeSeries.map((item) => ({
        period: item.month,
        label: formatMonthLabel(item.month),
        sentCount: item.sentCount,
        sentValue: { value: item.sentValue },
        acceptedCount: item.acceptedCount,
        acceptedValue: { value: item.acceptedValue },
        declinedCount: item.declinedCount,
        declinedValue: { value: item.declinedValue },
      })),
    };
  }, [data]);

  return {
    data,
    stats,
    ...rest,
  };
};

export default useGetDashboardProposalStats;
